import React from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { GlobalStyles } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RecoilRoot } from 'recoil';
import RouteProvider from './app/providers/RouteProvider/RouteProvider';
import AuthProvider from './app/providers/AuthProvider/AuthProvider';
import { theme } from './config/styles/theme';
import { tokensToCssVariables } from './styles/tokens';
import TranslationProvider from './app/providers/TranslationProvider/TranslationProvider';
import { Toaster } from 'sonner';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { isAxiosError } from 'axios';
import { isPrerender } from './utils/seo/prerender.util';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Never inside the prerenderer. It waits for every query to settle
        // before it snapshots, and a failing request (it aborts
        // /api/products/newest outright) would hold the page for the whole
        // 1+2+4s retry backoff - past its 12s budget under load.
        if (isPrerender()) return false;

        const status = isAxiosError(error) ? error.response?.status : undefined;
        // 404/410 are deterministic - retrying only delays the not-found UI
        // (and used to stall bot prerenders for seconds on dead SKUs)
        if (status === 404 || status === 410) return false;
        return failureCount < 3;
      },
    },
  },
});

// Design tokens as CSS custom properties on :root - the SCSS modules read them
// through _variables.scss, so they share one palette with the MUI theme.
const rootTokens = { ':root': tokensToCssVariables() };

const muiCache = createCache({
  key: 'mui',
  prepend: true,
});

const App = () => {
  return (
    <CacheProvider value={muiCache}>
      <MuiThemeProvider theme={theme}>
        <GlobalStyles styles={rootTokens} />
        <QueryClientProvider client={queryClient}>
          <RecoilRoot>
            <TranslationProvider>
              <AuthProvider>
                <RouteProvider />
                <Toaster />
              </AuthProvider>
            </TranslationProvider>
          </RecoilRoot>
        </QueryClientProvider>
      </MuiThemeProvider>
    </CacheProvider>
  );
};

export default App;
